import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { X } from "lucide-react";

interface TagInputProps {
  tags: string[];
  setTags: (tags: string[]) => void;
  placeholder?: string;
}

export const TagInput = React.memo<TagInputProps>(
  ({ tags, setTags, placeholder }) => {
    const [inputValue, setInputValue] = useState("");

    // Safety check: ensure tags is always an array
    const safeTags = tags || [];

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addTag();
      } else if (
        e.key === "Backspace" &&
        inputValue === "" &&
        safeTags.length > 0
      ) {
        removeTag(safeTags.length - 1);
      }
    };

    const addTag = () => {
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !safeTags.includes(trimmedValue)) {
        setTags([...safeTags, trimmedValue]);
        setInputValue("");
      }
    };

    const removeTag = (index: number) => {
      setTags(safeTags.filter((_, i) => i !== index));
    };

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-10.5 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          {safeTags.map((tag, index) => (
            <Badge
              key={`tag-${tag}-${index}`}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              <span>{tag}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeTag(index);
                }}
                className="ml-1 hover:bg-red-100 rounded-full p-0.5"
              >
                <X className="h-3 w-3 hover:text-red-500" />
              </button>
            </Badge>
          ))}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={addTag}
            placeholder={safeTags.length === 0 ? placeholder : ""}
            className="flex-1 outline-none bg-transparent min-w-30"
          />
        </div>
        <p className="text-xs text-gray-500">
          Press Enter or comma to add tags. Click the X to remove tags.
        </p>
      </div>
    );
  }
);

TagInput.displayName = "TagInput";
